import {
  Flex,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  Button,
  Text,
  FormControl,
  InputGroup,
  InputLeftAddon,
  Input,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import { Form, ErrorMessage, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { DisplayError } from 'components';
import { useAuthContext } from 'context/AuthProvider';
import { SUCCESS_TOAST } from 'utils';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { SmallSunWithCloudsIcon } from '../Icons';

const ShareProfileFormValidation = Yup.object().shape({
  emails: Yup.object().test(
    'object-check',
    'One value is required',
    (value) => {
      const check = Object.keys(value).every((email) => !value[email]);
      return !check;
    },
  ),
  inputEmail: Yup.string().email('Please enter a valid email address'),
});

const ShareProfile = ({ open, onClose }: ShareProfileProps): JSX.Element => {
  const { user } = useAuthContext();
  const toast = useToast();
  const [organizations, setOrganizations] = useState([] as Organization[]);

  useEffect(() => {
    if (user) {
      const params: OrganizationRequestParams = {
        limit: 5,
        order: 'numberOfCaregivers',
        orderBy: 'desc',
        userId: user?.uid,
      };
      axios({
        method: 'get',
        baseURL: process.env.REACT_APP_FUNCTIONS_URL,
        params: params,
        url: '/organizations',
      }).then((res) => {
        setOrganizations(res.data as Organization[]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendProfile = async (details: SendProfileDetails) => {
    await axios({
      baseURL: process.env.REACT_APP_FUNCTIONS_URL,
      url: '/profile/sendProfile',
      method: 'POST',
      data: {
        ...details,
      },
    });
  };

  const selectionsInvalid = (emails: IObjectKeys) => {
    return Object.keys(emails).every((email) => !emails[email]);
  };

  return (
    <Modal isOpen={open} onClose={() => onClose(false)}>
      <ModalOverlay />
      <ModalContent
        bg="custom.levoOffWhite"
        height="90%"
        mb={0}
        mt="auto"
        borderRadius="24px 24px 0 0"
      >
        <Formik
          validationSchema={ShareProfileFormValidation}
          initialValues={{
            emails: {
              ...organizations.reduce((acc, { email }) => {
                acc[email] = false;
                return acc;
              }, {} as IObjectKeys),
            },
            inputEmail: '',
          }}
          validateOnChange={true}
          onSubmit={async (values, { setStatus, setSubmitting }) => {
            setStatus(null);
            try {
              const uid = user?.uid;
              const emails = [];
              setSubmitting(true);
              for (const email of Object.keys(values.emails).filter(
                (email) => values.emails[email],
              )) {
                emails.push(email);
              }
              await sendProfile({
                userId: uid,
                emails,
              });
              toast({
                description: 'Profile successfully sent!',
                ...SUCCESS_TOAST,
              });
              setSubmitting(false);
              onClose(false);
            } catch (error) {
              setSubmitting(false);
              setStatus(error);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }: FormikProps<ShareProfileValues>) => (
            <Form
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Flex direction="column" width="100%">
                <Flex
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  p="24px 8px 24px 12px"
                >
                  <IconButton
                    variant="ghost"
                    aria-label="Close share profile modal"
                    icon={<CloseIcon boxSize="18px" />}
                    onClick={() => onClose(false)}
                    disabled={isSubmitting}
                  />
                  <Flex
                    position="absolute"
                    left={0}
                    right={0}
                    direction="column"
                    zIndex={-1}
                  >
                    <Text textStyle="heading3" fontWeight="bold" align="center">
                      Share profile
                    </Text>
                  </Flex>
                  <Button
                    variant="ghost"
                    color="custom.levoBlue"
                    cursor={
                      selectionsInvalid(values.emails) ? 'default' : 'pointer'
                    }
                    disabled={selectionsInvalid(values.emails)}
                    isLoading={isSubmitting}
                    _disabled={{ color: 'custom.levoGray' }}
                    onClick={() => handleSubmit()}
                  >
                    <Text textStyle="heading3" fontWeight="bold">
                      Send
                    </Text>
                  </Button>
                </Flex>
                <Flex
                  posiiton="relative"
                  direction="column"
                  overflow="hidden"
                  p="16px 24px"
                >
                  <FormControl id="inputEmail" mb={4}>
                    <Flex
                      direction="row"
                      flexWrap="wrap"
                      gridColumnGap="12px"
                      gridRowGap="8px"
                      bg="custom.levoAlabaster"
                      borderWidth="1px"
                      borderColor="custom.levoLightGray"
                      borderRadius="10px"
                      p={3}
                    >
                      {Object.keys(values.emails)
                        .filter((e) => values.emails[e])
                        .map((e) => (
                          <Flex
                            key={e}
                            bg="custom.levoLightYellow"
                            borderRadius="20px"
                            alignItems="center"
                            gridGap="8px"
                            p={2}
                          >
                            <IconButton
                              size="12px"
                              variant="ghost"
                              aria-label={`remove-${e}`}
                              icon={<CloseIcon boxSize="10px" />}
                              onClick={() => {
                                const { emails } = values;
                                delete emails[e];
                                setFieldValue('emails', { ...emails });
                              }}
                            />
                            <Text textStyle="text4">{e}</Text>
                          </Flex>
                        ))}
                      <InputGroup
                        display="flex"
                        gridGap="12px"
                        flexGrow={1}
                        variant="unstyled"
                        size={selectionsInvalid(values.emails) ? 'md' : 'sm'}
                        width="auto"
                      >
                        {selectionsInvalid(values.emails) && (
                          <InputLeftAddon borderRightWidth={0}>
                            To:&nbsp;
                          </InputLeftAddon>
                        )}
                        <Input
                          type="email"
                          onChange={handleChange}
                          onBlur={(event) => {
                            handleBlur(event);
                            if (!errors.inputEmail && values.inputEmail) {
                              const { emails } = values;
                              const newEmails = {
                                ...emails,
                                [values.inputEmail]: true,
                              };
                              setFieldValue('emails', newEmails);
                              setFieldValue('inputEmail', '');
                            }
                          }}
                          onKeyDown={(event) => {
                            if (!errors.inputEmail && values.inputEmail)
                              if (['Enter', 'Tab', ','].includes(event.key)) {
                                event.preventDefault();
                                const { emails } = values;
                                const newEmails = {
                                  ...emails,
                                  [values.inputEmail]: true,
                                };
                                setFieldValue('emails', newEmails);
                                setFieldValue('inputEmail', '');
                              }
                          }}
                          value={values.inputEmail}
                          isInvalid={!!errors.inputEmail && touched.inputEmail}
                          borderWidth={0}
                          outline={0}
                          placeholder={
                            selectionsInvalid(values.emails)
                              ? 'Email and/or company name'
                              : ''
                          }
                          _focus={{ outline: 0 }}
                        />
                      </InputGroup>
                    </Flex>
                    <ErrorMessage name="inputEmail">
                      {DisplayError}
                    </ErrorMessage>
                  </FormControl>
                  <Text textStyle="body" fontWeight="bold" mt={2} mb={4}>
                    Suggested companies
                  </Text>
                  <FormControl id="emails">
                    <Flex direction="column" gridGap="24px">
                      {organizations.map(({ id, email, name, location }) => (
                        <Flex
                          key={id}
                          height="42px"
                          bg="custom.levoAlabaster"
                          borderRadius="8px"
                          borderColor={
                            values.emails[email]
                              ? 'custom.levoYellow'
                              : 'custom.levoLightBlack'
                          }
                          borderWidth={values.emails[email] ? '2px' : '1px'}
                          justifyContent="space-between"
                          alignItems="center"
                          gridGap="10px"
                          pl={3}
                        >
                          <Text
                            textStyle="body"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                          >
                            {name}, {location}
                          </Text>
                          {!values.emails[email] && (
                            <Button
                              display="flex"
                              gridGap="8px"
                              variant="ghost"
                              color="custom.levoBlue"
                              borderRadius="0 8px 8px 0"
                              onClick={() => {
                                const { emails } = values;
                                const currentVal = emails[email];
                                const newEmails = {
                                  ...emails,
                                  [email]: !currentVal,
                                };
                                setFieldValue('emails', newEmails);
                              }}
                              px={2}
                            >
                              <AddIcon />
                              <Text textStyle="body" fontWeight="bold">
                                {!values.emails[email] ? 'Add' : 'Remove'}
                              </Text>
                            </Button>
                          )}
                        </Flex>
                      ))}
                    </Flex>
                  </FormControl>
                  <Flex
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    width="100%"
                    direction="column"
                    zIndex={-1}
                  >
                    <SmallSunWithCloudsIcon height="auto" width="auto" />
                  </Flex>
                </Flex>
              </Flex>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
};

export default ShareProfile;
